import React, {
  useContext,
  useEffect,
  useState
} from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Tooltip
} from "@material-ui/core";

import {
  FileCopy
} from "@material-ui/icons";

import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

import {
  WhatsAppsContext
} from "../../context/WhatsApp/WhatsAppsContext";


const ApiConnectionModal = ({
  open,
  onClose,
  apiConnectionId,
  onSave
}) => {

  const {
    whatsApps
  } = useContext(WhatsAppsContext);


  const [name, setName] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const [generatedToken, setGeneratedToken] =
    useState("");


  const isEditing = Boolean(apiConnectionId);


  /* ==========================================================
     CARGAR DATOS PARA EDITAR
  ========================================================== */

  useEffect(() => {

    if (!open) {
      return;
    }

    setGeneratedToken("");


    if (!apiConnectionId) {

      setName("");
      setWhatsappId("");
      setIsActive(true);

      return;
    }


    const loadApiConnection = async () => {

      setLoadingData(true);

      try {

        const {
          data
        } = await api.get(
          `/api/api-connections/${apiConnectionId}`
        );


        setName(
          data.name || ""
        );


        setWhatsappId(
          data.whatsappId ||
          data.whatsapp?.id ||
          ""
        );


        setIsActive(
          data.isActive !== false
        );

      } catch (err) {

        toastError(err);

      } finally {

        setLoadingData(false);

      }
    };


    loadApiConnection();

  }, [
    open,
    apiConnectionId
  ]);


  /* ==========================================================
     CERRAR
  ========================================================== */

  const handleClose = () => {

    if (loading) {
      return;
    }

    setGeneratedToken("");

    setName("");
    setWhatsappId("");
    setIsActive(true);

    onClose();
  };


  /* ==========================================================
     GUARDAR
  ========================================================== */

  const handleSubmit = async () => {

    if (!name.trim()) {

      toast.error(
        "Ingresa un nombre para la conexión."
      );

      return;
    }


    if (!whatsappId) {

      toast.error(
        "Selecciona una conexión de WhatsApp."
      );

      return;
    }


    setLoading(true);


    try {

      /* ==================================================
         EDITAR
      ================================================== */

      if (isEditing) {

        await api.put(
          `/api/api-connections/${apiConnectionId}`,
          {
            name: name.trim(),
            whatsappId: Number(whatsappId),
            isActive
          }
        );


        toast.success(
          "Conexión API actualizada."
        );


        if (onSave) {
          await onSave();
        }


        handleClose();

        return;
      }


      /* ==================================================
         CREAR
      ================================================== */

      const {
        data
      } = await api.post(
        "/api/api-connections",
        {
          name: name.trim(),
          whatsappId: Number(whatsappId),
          isActive
        }
      );


      /* ==================================================
         TOKEN GENERADO
      ================================================== */

      setGeneratedToken(
        data.token || ""
      );


      toast.success(
        "Conexión API creada."
      );


      if (onSave) {
        await onSave();
      }

    } catch (err) {

      toastError(err);

    } finally {

      setLoading(false);

    }
  };


  /* ==========================================================
     COPIAR TOKEN
  ========================================================== */

  const handleCopyToken = async () => {

    if (!generatedToken) {
      return;
    }


    try {

      await navigator.clipboard.writeText(
        generatedToken
      );

      toast.success(
        "Token copiado."
      );

    } catch (err) {

      toast.error(
        "No se pudo copiar el token."
      );

    }
  };


  /* ==========================================================
     FINALIZAR CREACIÓN
  ========================================================== */

  const handleAfterCreateClose = () => {

    setGeneratedToken("");

    setName("");
    setWhatsappId("");
    setIsActive(true);

    onClose();
  };


  /* ==========================================================
     MODAL
  ========================================================== */

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >

      <DialogTitle>

        {generatedToken
          ? "Conexión API creada"
          : isEditing
          ? "Editar conexión API"
          : "Nueva conexión API"}

      </DialogTitle>


      <DialogContent>

        {/* ==================================================
            CARGANDO
        ================================================== */}

        {loadingData && (

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding={4}
          >

            <CircularProgress />

          </Box>

        )}


        {/* ==================================================
            TOKEN GENERADO
        ================================================== */}

        {!loadingData && generatedToken && (

          <Box paddingTop={1}>

            <Typography
              variant="body1"
              gutterBottom
            >
              La conexión API fue creada
              correctamente.
            </Typography>


            <Typography
              variant="body2"
              color="textSecondary"
              gutterBottom
            >
              Guarda este token. Lo utilizarás
              para autenticar las solicitudes
              de Zabbix, PRTG, Grafana,
              Uptime Kuma u otros sistemas
              externos.
            </Typography>


            <Box
              display="flex"
              alignItems="center"
              marginTop={2}
            >

              <TextField
                fullWidth
                variant="outlined"
                label="Token"
                value={generatedToken}
                InputProps={{
                  readOnly: true
                }}
              />


              <Tooltip title="Copiar token">

                <IconButton
                  onClick={
                    handleCopyToken
                  }
                  style={{
                    marginLeft: 8
                  }}
                >

                  <FileCopy />

                </IconButton>

              </Tooltip>

            </Box>


            <Box marginTop={2}>

              <Typography
                variant="caption"
                color="error"
              >
                Importante: guarda este
                token en un lugar seguro.
                No lo compartas públicamente.
              </Typography>

            </Box>

          </Box>

        )}


        {/* ==================================================
            FORMULARIO
        ================================================== */}

        {!loadingData && !generatedToken && (

          <Box paddingTop={1}>

            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              margin="dense"
              label="Nombre"
              value={name}
              onChange={e =>
                setName(
                  e.target.value
                )
              }
              placeholder="Ej. Zabbix Producción"
            />


            <FormControl
              fullWidth
              variant="outlined"
              margin="dense"
              style={{
                marginTop: 16
              }}
            >

              <InputLabel>
                WhatsApp
              </InputLabel>


              <Select
                value={whatsappId}
                onChange={e =>
                  setWhatsappId(
                    e.target.value
                  )
                }
                label="WhatsApp"
              >

                {whatsApps &&
                  whatsApps.map(
                    whatsApp => (

                      <MenuItem
                        key={
                          whatsApp.id
                        }
                        value={
                          whatsApp.id
                        }
                      >
                        {
                          whatsApp.name
                        }
                      </MenuItem>

                    )
                  )}

              </Select>

            </FormControl>


            <FormControlLabel
              style={{
                marginTop: 8
              }}
              control={

                <Switch
                  checked={
                    isActive
                  }
                  onChange={e =>
                    setIsActive(
                      e.target.checked
                    )
                  }
                  color="primary"
                />

              }
              label={
                isActive
                  ? "Conexión activa"
                  : "Conexión inactiva"
              }
            />

          </Box>

        )}

      </DialogContent>


      <DialogActions>

        {/* ==================================================
            TOKEN
        ================================================== */}

        {generatedToken && (

          <Button
            onClick={
              handleAfterCreateClose
            }
            color="primary"
            variant="contained"
          >
            Finalizar
          </Button>

        )}


        {/* ==================================================
            FORMULARIO
        ================================================== */}

        {!generatedToken && (

          <React.Fragment>

            <Button
              onClick={handleClose}
              color="default"
              disabled={loading}
            >
              Cancelar
            </Button>


            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={
                loading ||
                loadingData
              }
            >

              {loading ? (

                <CircularProgress
                  size={24}
                />

              ) : isEditing ? (

                "Guardar cambios"

              ) : (

                "Crear conexión"

              )}

            </Button>

          </React.Fragment>

        )}

      </DialogActions>

    </Dialog>
  );
};


export default ApiConnectionModal;